import { useState } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { IUserAddRequest, IUserAddResult } from '../../../utils/constants/interfaces';

const defaultUser: IUserAddRequest = {
  id: '',
  emailAddress: '',
  firstName: '',
  lastName: '',
  cellPhone: '',
  permissions: [],
  sendFirstTimeEmail: false
};

const useUserQuickAdd = (
  onUserAdded: (userId: string) => void,
  onCancel: (() => void) | undefined
) => {
  const api = useApi();
  const [userAddRequest, setUserAddRequest] = useState<IUserAddRequest>(defaultUser);
  const [alertMessage, setAlertMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);

  const updateValue = (key: string, value: string | boolean | string[] | null): void => {
    if (isSaving) return;
    setAlertMessage('');

    let errors = [...fieldErrors];
    errors = errors.filter((x) => !x.endsWith(`:${key}`));
    setFieldErrors(errors);

    setUserAddRequest((user) => {
      const editUser = { ...user };
      editUser[key] = value;
      return editUser;
    });
  };

  const toggleSendFirstTimeEmail = () => {
    setUserAddRequest((user) => ({ ...user, sendFirstTimeEmail: !user.sendFirstTimeEmail }));
  };

  const validate = () => {
    const errors: string[] = [];
    if (
      Helpers.isNullOrWhiteSpace(userAddRequest.emailAddress) ||
      !Helpers.isValidEmail(userAddRequest.emailAddress)
    ) {
      errors.push('overview:emailAddress');
    }
    if (Helpers.isNullOrWhiteSpace(userAddRequest.firstName)) {
      errors.push('overview:firstName');
    }
    if (Helpers.isNullOrWhiteSpace(userAddRequest.lastName)) {
      errors.push('overview:lastName');
    }
    setFieldErrors(errors);
    return errors.length === 0;
  };

  const saveChanges = async (): Promise<void> => {
    if (isSaving) return;

    if (!validate()) {
      return;
    }
    setIsSaving(true);
    const apiResponse = await api.post<IUserAddResult>('/user', JSON.stringify(userAddRequest));
    if (apiResponse.isSuccessStatusCode && apiResponse.response) {
      if (apiResponse.response.succeeded) {
        if (onUserAdded) {
          onUserAdded(apiResponse.response.userId ?? '');
        }
      } else {
        setAlertMessage(apiResponse.response.message);
      }
    } else {
      setAlertMessage('Error creating new user.');
    }
    setIsSaving(false);
  };

  return {
    isSaving,
    fieldErrors,
    alertMessage,
    userAddRequest,
    updateValue,
    saveChanges,
    toggleSendFirstTimeEmail
  };
};

export default useUserQuickAdd;
